// Compact codec for serialising quiz answers into the URL fragment so
// quiz state survives refresh, deep-linking, and cross-device sharing.
//
// Format: base64url(JSON of a {qid: {o?, b?}} map). The flat layout drops the
// redundant `questionId` field that lives inside each Answer; we re-add it on
// decode. Empty answers are omitted to keep the URL short.
//
// Example payload before encoding:
//   { "passport": {"o":["eu"]}, "budget": {"b": 3}, "climate": {"o":["alps","mediterranean"]} }
//
// Typical encoded length: 120–250 chars for a fully-answered quiz.

import type { Answers, BudgetBucket, QuestionId } from './types';

type CompactAnswer = { o?: string[]; b?: BudgetBucket };

function base64UrlEncode(input: string): string {
  // btoa needs binary string; we already pass UTF-8 via encodeURIComponent trick
  // to handle non-ASCII safely (just in case future answers contain Cyrillic).
  const bytes = new TextEncoder().encode(input);
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64UrlDecode(input: string): string {
  const pad = input.length % 4 === 0 ? '' : '='.repeat(4 - (input.length % 4));
  const b64 = input.replace(/-/g, '+').replace(/_/g, '/') + pad;
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new TextDecoder().decode(bytes);
}

export function encodeAnswersForUrl(answers: Answers): string {
  const flat: Record<string, CompactAnswer> = {};
  for (const [qid, ans] of Object.entries(answers)) {
    if (!ans) continue;
    if (ans.budgetBucket !== undefined) {
      flat[qid] = { b: ans.budgetBucket };
    } else if (ans.optionIds.length > 0) {
      flat[qid] = { o: ans.optionIds };
    }
  }
  return base64UrlEncode(JSON.stringify(flat));
}

export function decodeAnswersFromUrl(blob: string): Answers | null {
  if (!blob) return null;
  try {
    const json = base64UrlDecode(blob);
    const parsed = JSON.parse(json) as Record<string, CompactAnswer>;
    if (typeof parsed !== 'object' || parsed === null) return null;
    const out: Answers = {};
    for (const [qid, val] of Object.entries(parsed)) {
      if (val.b !== undefined) {
        out[qid as QuestionId] = {
          questionId: qid as QuestionId,
          optionIds: [],
          budgetBucket: val.b,
        };
      } else if (Array.isArray(val.o)) {
        out[qid as QuestionId] = {
          questionId: qid as QuestionId,
          optionIds: val.o,
        };
      }
    }
    return out;
  } catch {
    return null;
  }
}
